import { faFilePdf } from '@fortawesome/pro-regular-svg-icons/faFilePdf';
import { faImages } from '@fortawesome/pro-regular-svg-icons/faImages';
import { faPictureInPicture } from '@fortawesome/pro-regular-svg-icons/faPictureInPicture';
import { faVideo } from '@fortawesome/pro-regular-svg-icons/faVideo';
import { faVrCardboard } from '@fortawesome/pro-regular-svg-icons/faVrCardboard';
import useSlideshowBar from '@propertypal/shared/src/hooks/useSlideshowBar';
import SitemapIcon from '@propertypal/shared/src/resources/icons/property/sitemap-black.svg';
import { Property } from '@propertypal/shared/src/types';
import { useRouter } from 'next/router';
import React from 'react';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import { Button, SlideshowGrid } from './PropertySlideshowBar.style';

interface PropertySlideshowBarProps {
  property: Property;
  getReplaceUrl: (url: string) => string;
}

const PropertySlideshowBar: React.FC<PropertySlideshowBarProps> = (props) => {
  const router = useRouter();
  const { propertyVideo, propertyTour, images, propertyFloorPlans, handleTourClick, tourPath, videoPath, trackLink } =
    useSlideshowBar({
      property: props.property,
      getReplaceUrl: props.getReplaceUrl,
      location: 'Pinned Property Type',
    });

  return (
    <SlideshowGrid data-testid="property-box-slideshow-bar" className="hide-print">
      {propertyVideo && (
        <Button
          id={`pinned-property-${videoPath}`}
          data-testid="slideshow-video-button"
          type="button"
          tabIndex={-1}
          onClick={(e) => {
            e.preventDefault();
            trackLink('Video')();
            router.replace(props.getReplaceUrl(`/${videoPath}`), undefined, { shallow: false });
          }}
        >
          <FontAwesomeIcon icon={faVideo} size={18} />
          <span>Video</span>
        </Button>
      )}

      {propertyTour && tourPath && (
        <Button
          id={`pinned-property-${tourPath}`}
          data-testid="slideshow-tour-button"
          type="button"
          tabIndex={-1}
          onClick={(e) => {
            e.preventDefault();
            handleTourClick();
          }}
        >
          <FontAwesomeIcon icon={faVrCardboard} size={18} />
          <span>Virtual Tour</span>
        </Button>
      )}

      {images.length > 0 && (
        <Button
          id="pinned-property-tab-gallery"
          data-testid="slideshow-gallery-button"
          type="button"
          tabIndex={-1}
          onClick={(e) => {
            e.preventDefault();
            trackLink('Gallery')();
            router.replace(props.getReplaceUrl('/gallery'), undefined, { shallow: false });
          }}
        >
          <FontAwesomeIcon icon={faImages} size={18} />
          <span>Gallery</span>
        </Button>
      )}

      {!!props.property.images?.find((i) => i.imageType === 'SiteMap') && (
        <Button
          id="pinned-property-tab-sitemap"
          data-testid="slideshow-site-map-button"
          type="button"
          tabIndex={-1}
          onClick={(e) => {
            e.preventDefault();
            trackLink('Site Map')();
            router.replace(props.getReplaceUrl('/sitemap'), undefined, { shallow: false });
          }}
        >
          <SitemapIcon />
          <span>Site Map</span>
        </Button>
      )}

      {!!propertyFloorPlans?.length && (
        <Button
          id="pinned-property-tab-floor-plans"
          data-testid="slideshow-floor-plans-button"
          type="button"
          tabIndex={-1}
          onClick={(e) => {
            e.preventDefault();
            trackLink('Floor Plans')();
            router.replace(props.getReplaceUrl('/floor-plans'), undefined, { shallow: false });
          }}
        >
          <FontAwesomeIcon icon={faPictureInPicture} size={18} />
          <span>Floor Plans</span>
        </Button>
      )}

      {!!props.property.hasBrochures && (
        <Button
          id="pinned-property-tab-brochure"
          data-testid="slideshow-brochure-button"
          type="button"
          tabIndex={-1}
          onClick={(e) => {
            e.preventDefault();
            trackLink('Brochure')();
            router.replace(props.getReplaceUrl('/brochure/1'), undefined, { shallow: false });
          }}
        >
          <FontAwesomeIcon icon={faFilePdf} size={18} />
          <span>Brochure</span>
        </Button>
      )}
    </SlideshowGrid>
  );
};

export default PropertySlideshowBar;
